import React, { useState, useEffect, useRef, useCallback } from 'react';
import { describeCallError } from './callErrors';
import PropTypes from 'prop-types';
import CallAudioOutput from './CallAudioOutput';
import { browserCallParameters } from '../../utils/browserCall';
import { Modal, Button } from 'react-bootstrap';
import LiveCallCoach from './LiveCallCoach';
import {
  TelephoneFill,
  TelephoneXFill,
  TelephoneInboundFill,
  MicFill,
  MicMuteFill,
} from 'react-bootstrap-icons';
import { getVoiceToken, logBrowserCall } from '../../api/communicationApi';
import { useAuth } from '../userManagement/AuthContext';
import { hasProtectedContacts, maskedPhone } from '../../utils/customerPrivacy';
import T, { useAutoTranslate } from '../common/T';

/* global Twilio */

const CALL_STATES = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  RINGING: 'ringing',
  INCOMING: 'incoming',
  CONNECTED: 'connected',
  ENDED: 'ended',
  ERROR: 'error',
};

const CallModal = ({ show, onHide, conversationId, userId, customerName, userDisplayName, incomingCall }) => {
  const at = useAutoTranslate();
  const { user } = useAuth();
  const protectedContacts = hasProtectedContacts(user);
  const [callState, setCallState] = useState(CALL_STATES.IDLE);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [callError, setCallError] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');

  const callRef = useRef(null);
  const deviceRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const userDisplayRef = useRef(userDisplayName);
  userDisplayRef.current = userDisplayName;

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (callRef.current) {
      try { callRef.current.disconnect(); } catch (_) { /* Already disconnected. */ }
      callRef.current = null;
    }
    if (deviceRef.current) {
      try { deviceRef.current.disconnectAll(); } catch (_) { /* No active connection. */ }
      try { deviceRef.current.destroy(); } catch (_) { /* Already destroyed. */ }
      deviceRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
  }, []);

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Attach event listeners to a call object
  const attachCallEvents = useCallback((call, cancelledRef) => {
    call.on('accept', function () {
      if (!cancelledRef.current) {
        setCallState(CALL_STATES.CONNECTED);
        startTimer();
      }
    });

    call.on('ringing', function () {
      if (!cancelledRef.current) setCallState(CALL_STATES.RINGING);
    });

    call.on('disconnect', function () {
      if (!cancelledRef.current) {
        setCallState(CALL_STATES.ENDED);
        if (timerRef.current) clearInterval(timerRef.current);
      }
    });

    call.on('cancel', function () {
      if (!cancelledRef.current) {
        setCallState(CALL_STATES.ENDED);
        if (timerRef.current) clearInterval(timerRef.current);
      }
    });

    call.on('error', function (err) {
      console.error('Call error:', err);
      if (!cancelledRef.current) {
        if (timerRef.current) clearInterval(timerRef.current);
        var code = err && err.code;
        if (code === 31603 || code === 31005) {
          setCallState(CALL_STATES.ENDED);
        } else {
          setErrorMsg(err.message || 'Call failed');
          setCallState(CALL_STATES.ERROR);
        }
      }
    });
  }, [startTimer]);

  // Handle incoming call
  useEffect(() => {
    if (!show || !incomingCall) return;

    setCallState(CALL_STATES.INCOMING);
    const incomingNumber = incomingCall.customParameters?.get('PhoneDisplay') || incomingCall.parameters?.From || '';
    setPhoneNumber(protectedContacts ? maskedPhone(incomingNumber) : incomingNumber);
    setErrorMsg('');
    setDuration(0);
    callRef.current = incomingCall;

    const cancelledRef = { current: false };
    attachCallEvents(incomingCall, cancelledRef);

    return () => {
      cancelledRef.current = true;
    };
  }, [show, incomingCall, attachCallEvents, protectedContacts]);

  const handleAcceptIncoming = () => {
    if (callRef.current && callRef.current.accept) {
      callRef.current.accept();
      setCallState(CALL_STATES.CONNECTED);
      startTimer();
    }
  };

  const handleRejectIncoming = () => {
    if (callRef.current && callRef.current.reject) {
      callRef.current.reject();
    }
    setCallState(CALL_STATES.ENDED);
  };

  // Start outbound call when modal opens (only if no incoming call)
  useEffect(() => {
    if (!show || !conversationId || incomingCall) return;

    let cancelled = false;
    const cancelledRef = { current: false };

    const initCall = async () => {
      try {
        setCallState(CALL_STATES.CONNECTING);
        setErrorMsg('');
        setDuration(0);

        // 1. Get voice token + phone number from backend
        const data = await getVoiceToken(conversationId, userId);
        if (cancelled) return;

        const display = data.phone_display || data.phone_number;
        setPhoneNumber(protectedContacts ? maskedPhone(display) : display);

        // 2. Create Twilio Device with token (v2.x API via CDN global)
        if (!Twilio || !Twilio.Device) {
          throw new Error('Twilio Voice SDK not loaded. Check your internet connection.');
        }
        const device = new Twilio.Device(data.token, {
          logLevel: 'warn',
          codecPreferences: ['opus', 'pcmu'],
        });
        deviceRef.current = device;

        device.on('error', function (twilioError) {
          console.error('Twilio Device error:', twilioError);
          if (!cancelled) {
            setErrorMsg(twilioError.message || 'Twilio device error');
            setCallError(null);
            setCallState(CALL_STATES.ERROR);
          }
        });

        // Outgoing calls connect directly; registration opts into incoming calls.
        if (cancelled) return;

        setCallState(CALL_STATES.RINGING);

        const call = await device.connect({
          params: browserCallParameters(data, userDisplayRef.current, conversationId),
        });
        callRef.current = call;
        attachCallEvents(call, cancelledRef);

      } catch (err) {
        if (!cancelled) {
          console.error('Call init error:', err);
          // A refusal, a provider that is not set up and a crash are three
          // different messages; printing the raw detail made all of them look
          // like a fault in the system.
          setCallError(describeCallError(err));
          setCallState(CALL_STATES.ERROR);
        }
      }
    };

    initCall();

    return () => {
      cancelled = true;
      cancelledRef.current = true;
      cleanup();
    };
  }, [show, conversationId, userId, incomingCall, cleanup, startTimer, attachCallEvents, protectedContacts]);

  const handleHangup = () => {
    if (callRef.current) {
      callRef.current.disconnect();
    }
    if (deviceRef.current) {
      try { deviceRef.current.disconnectAll(); } catch (_) { /* No active connection. */ }
    }
    setCallState(CALL_STATES.ENDED);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleMuteToggle = () => {
    if (callRef.current) {
      const newMuted = !isMuted;
      callRef.current.mute(newMuted);
      setIsMuted(newMuted);
    }
  };

  const handleClose = async () => {
    // Log the call to the conversation timeline before closing
    if (conversationId && userId && phoneNumber && callState !== CALL_STATES.IDLE) {
      try {
        const callStatus = callState === CALL_STATES.ERROR ? 'failed'
          : duration > 0 ? 'completed'
          : 'no-answer';
        // Get call SID from the Twilio call object for linking with existing CallRecord
        const callSid = callRef.current?.parameters?.CallSid || null;
        await logBrowserCall(conversationId, {
          sender_user_id: userId,
          sender_display: userDisplayName || 'Agent',
          duration_seconds: duration,
          call_status: callStatus,
          call_sid: callSid,
        });
      } catch (err) {
        console.error('Failed to log browser call:', err);
      }
    }
    cleanup();
    setCallState(CALL_STATES.IDLE);
    setDuration(0);
    setIsMuted(false);
    setErrorMsg('');
    setPhoneNumber('');
    onHide();
  };

  const isActive = [CALL_STATES.CONNECTING, CALL_STATES.RINGING, CALL_STATES.INCOMING, CALL_STATES.CONNECTED].includes(callState);
  const isIncoming = callState === CALL_STATES.INCOMING;

  return (
    <Modal
      show={show}
      onHide={isActive ? undefined : handleClose}
      centered
      backdrop="static"
      keyboard={false}
      size={callState === CALL_STATES.CONNECTED ? 'lg' : 'sm'}
    >
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        borderRadius: 8,
        color: '#fff',
        padding: '40px 30px',
        textAlign: 'center',
        minHeight: 380,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        {/* Avatar / Pulse */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: callState === CALL_STATES.CONNECTED
              ? 'rgba(40, 167, 69, 0.3)'
              : 'rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: (callState === CALL_STATES.RINGING || callState === CALL_STATES.INCOMING) ? 'pulse-ring 1.5s infinite' : 'none',
          }}>
            <TelephoneFill size={32} color={
              callState === CALL_STATES.CONNECTED ? '#28a745' : '#fff'
            } />
          </div>
        </div>

        {/* Customer Info */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
            {customerName || 'Customer'}
          </div>
          <div style={{ fontSize: 14, opacity: 0.7 }}>
            {(protectedContacts ? maskedPhone(phoneNumber) : phoneNumber) || '...'}
          </div>
        </div>

        {/* Status */}
        <div style={{ fontSize: 16, marginBottom: 20 }}>
          {callState === CALL_STATES.CONNECTING && (
            <span style={{ color: '#ffc107' }}><T>Connecting...</T></span>
          )}
          {callState === CALL_STATES.RINGING && (
            <span style={{ color: '#ffc107' }}><T>Ringing...</T></span>
          )}
          {callState === CALL_STATES.INCOMING && (
            <span style={{ color: '#17a2b8' }}><T>Incoming call...</T></span>
          )}
          {callState === CALL_STATES.CONNECTED && (
            <span style={{ color: '#28a745', fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {formatDuration(duration)}
            </span>
          )}
          {callState === CALL_STATES.ENDED && (
            <span style={{ color: '#aaa' }}><T>Call ended &middot; </T>{formatDuration(duration)}</span>
          )}
          {callState === CALL_STATES.ERROR && (
            <div style={{ textAlign: 'center', maxWidth: 280 }}>
              <div style={{ color: '#f87171', fontSize: 14, fontWeight: 700 }}>
                {(callError && callError.title) || errorMsg || 'The call could not be started'}
              </div>
              {callError && callError.hint && (
                <div style={{ color: '#cbd5e1', fontSize: 12, marginTop: 4, lineHeight: 1.35 }}>
                  {callError.hint}
                </div>
              )}
              {callError && callError.detail && (
                <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 6 }}>
                  {callError.detail}
                </div>
              )}
            </div>
          )}
        </div>

        {callState === CALL_STATES.CONNECTED && <CallAudioOutput audio={deviceRef.current?.audio} tone="dark" />}

        {/* Controls */}
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 16 }}>
          {isIncoming && (
            <>
              <button
                onClick={handleRejectIncoming}
                title={at("Reject")}
                style={{
                  width: 64, height: 64, borderRadius: '50%',
                  border: 'none', cursor: 'pointer',
                  background: '#dc3545', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(220, 53, 69, 0.5)',
                  transition: 'all 0.2s',
                }}
              >
                <TelephoneXFill size={26} />
              </button>

              <button
                onClick={handleAcceptIncoming}
                title={at("Accept")}
                style={{
                  width: 64, height: 64, borderRadius: '50%',
                  border: 'none', cursor: 'pointer',
                  background: '#28a745', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(40, 167, 69, 0.5)',
                  transition: 'all 0.2s',
                }}
              >
                <TelephoneInboundFill size={26} />
              </button>
            </>
          )}

          {isActive && !isIncoming && (
            <>
              <button
                onClick={handleMuteToggle}
                title={isMuted ? 'Unmute' : 'Mute'}
                style={{
                  width: 52, height: 52, borderRadius: '50%',
                  border: 'none', cursor: 'pointer',
                  background: isMuted ? 'rgba(220, 53, 69, 0.3)' : 'rgba(255,255,255,0.15)',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
              >
                {isMuted ? <MicMuteFill size={22} /> : <MicFill size={22} />}
              </button>

              <button
                onClick={handleHangup}
                title={at("Hang up")}
                style={{
                  width: 64, height: 64, borderRadius: '50%',
                  border: 'none', cursor: 'pointer',
                  background: '#dc3545', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(220, 53, 69, 0.5)',
                  transition: 'all 0.2s',
                }}
              >
                <TelephoneXFill size={26} />
              </button>


            </>
          )}

          {(callState === CALL_STATES.ENDED || callState === CALL_STATES.ERROR) && (
            <Button
              variant="outline-light"
              onClick={handleClose}
              style={{ borderRadius: 24, padding: '8px 32px' }}
            ><T>
              Close
            </T></Button>
          )}
        </div>
      </div>

      {callState === CALL_STATES.CONNECTED && <LiveCallCoach conversationId={conversationId} callSid={callRef.current?.parameters?.CallSid} />}
      <style>{`
        @keyframes pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.5); }
          70% { box-shadow: 0 0 0 20px rgba(255, 193, 7, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); }
        }
      `}</style>
    </Modal>
  );
};

CallModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  conversationId: PropTypes.string,
  userId: PropTypes.string,
  customerName: PropTypes.string,
  userDisplayName: PropTypes.string,
  incomingCall: PropTypes.shape({
    parameters: PropTypes.shape({ From: PropTypes.string }),
    customParameters: PropTypes.shape({ get: PropTypes.func }),
  }),
};

export default CallModal;
